/**
 * Clients — list + create/edit/delete modal.
 *
 * Modelo de jerarquía (dropshipping):
 *   Un cliente puede ser `mayorista` y tener clientes finales ligados (hijos)
 *   que reciben el envío directo. Cada hijo es a su vez un cliente completo
 *   con su propio historial. La relación se guarda en `parentClientId` del
 *   hijo. Profundidad máxima = 2 (un hijo no puede tener hijos).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Building2,
  Check,
  Link2,
  Link2Off,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  CLIENT_TYPES,
  CLIENT_TYPE_LEGACY_MAP,
} from '@expogreens/shared'
import { PageHeader } from '@/components/ui/PageHeader'
import { ExpandableCards, CardField, CardGrid } from '@/components/ui/ExpandableCards'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'

const labelForType = (t) => CLIENT_TYPES[t] || t || '—'
const canonicalType = (t) => CLIENT_TYPE_LEGACY_MAP[t] || t || 'principal'

// Opciones para el selector visual del tipo de cliente. Cada una con
// icono propio para que el usuario las distinga de un vistazo sin abrir
// un dropdown.
const CLIENT_TYPE_OPTIONS = [
  {
    id: 'principal',
    label: 'Principal',
    icon: Building2,
    description: 'Cliente principal — puede tener consignatarios ligados.',
  },
  {
    id: 'consignee',
    label: 'Consignatario',
    icon: MapPin,
    description: 'Ubicación/consignatario asociado a un cliente Principal.',
  },
]

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  nit: '',
  address: '',
  clientType: 'principal',
  parentClientId: '',
}

export default function ClientsPage() {
  const { data, loading, error, refresh } = useApi('/clients')
  const [editing, setEditing] = useState(null) // null | EMPTY | existing row

  // Estado del modal: padre propuesto + hijos propuestos (por id). Se
  // separan del estado del form principal porque los hijos viven en
  // registros OTROS (no en este cliente), y se aplican via PUT por cada
  // hijo al guardar.
  const [parentId, setParentId] = useState('')
  const [childIds, setChildIds] = useState([])
  const [childPick, setChildPick] = useState('')
  // Tipo de cliente — controlado para soportar el selector visual con
  // tarjetas-botón (en vez de un <select>).
  const [clientType, setClientType] = useState('principal')

  // Sincronizar el tipo con el cliente que se está editando.
  useEffect(() => {
    if (!editing) return
    setClientType(canonicalType(editing.clientType))
  }, [editing])

  // Auto-coherencia: si el usuario marca al cliente como Consignatario
  // de uno principal, su tipo pasa a 'consignee'. Y al revés, si saca
  // el padre, vuelve a 'principal'. Esto evita estados inconsistentes
  // sin que el usuario tenga que recordar tocar dos campos.
  useEffect(() => {
    if (parentId && clientType !== 'consignee') setClientType('consignee')
    if (!parentId && clientType === 'consignee') setClientType('principal')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId])

  const save = useMutation(async (payload) => {
    if (payload.id) {
      return apiCall(`/clients/${payload.id}`, { method: 'PUT', body: payload })
    }
    return apiCall('/clients', { method: 'POST', body: payload })
  })

  const remove = useMutation(async (id) => apiCall(`/clients/${id}`, { method: 'DELETE' }))

  // Mapa id→cliente para resolver nombres rápido.
  const byId = useMemo(() => {
    const m = new Map()
    for (const c of data || []) m.set(c.id, c)
    return m
  }, [data])

  // ¿Este cliente es padre de alguien? (mientras lo sea no puede tener padre)
  const hasChildren = (clientId) =>
    (data || []).some((c) => c.parentClientId === clientId)

  const childrenOf = (clientId) =>
    (data || []).filter((c) => c.parentClientId === clientId)

  const openCreate = () => {
    setEditing({ ...EMPTY })
    setParentId('')
    setChildIds([])
    setChildPick('')
  }

  const openEdit = (row) => {
    setEditing(row)
    setParentId(row.parentClientId || '')
    setChildIds(childrenOf(row.id).map((c) => c.id))
    setChildPick('')
  }

  const closeModal = () => {
    setEditing(null)
    setParentId('')
    setChildIds([])
    setChildPick('')
  }

  // Candidatos a mayorista (padre): clientes ≠ self que NO son hijos de otro
  // y que no están en la lista de hijos propuestos del actual (no pueden ser
  // padre de su propio padre).
  const parentCandidates = useMemo(() => {
    if (!editing) return []
    return (data || []).filter((c) => {
      if (editing.id && c.id === editing.id) return false
      if (c.parentClientId) return false
      if (childIds.includes(c.id)) return false
      return true
    })
  }, [data, editing, childIds])

  // Candidatos a hijo: clientes ≠ self, no ya seleccionados, sin padre (para
  // no quitarle el padre a otro), y ellos mismos no son padres de nadie (o
  // sea, no tienen hijos).
  const childCandidates = useMemo(() => {
    if (!editing) return []
    return (data || []).filter((c) => {
      if (editing.id && c.id === editing.id) return false
      if (childIds.includes(c.id)) return false
      if (c.parentClientId && c.parentClientId !== editing.id) return false
      if (hasChildren(c.id)) return false
      // Si yo mismo tengo padre, no puedo tener hijos.
      if (parentId) return false
      return true
    })
  }, [data, editing, childIds, parentId]) // eslint-disable-line react-hooks/exhaustive-deps

  const addChild = () => {
    if (!childPick) return
    setChildIds((l) => (l.includes(childPick) ? l : [...l, childPick]))
    setChildPick('')
  }
  const removeChild = (id) => setChildIds((l) => l.filter((x) => x !== id))

  const onSubmit = async (e) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const payload = Object.fromEntries(fd.entries())
    if (editing?.id) payload.id = editing.id
    payload.parentClientId = parentId || null
    // El tipo viene del selector visual (state) — sobrescribe cualquier
    // resto antiguo que pudiera venir del FormData.
    payload.clientType = clientType

    try {
      const saved = await save.mutate(payload)
      const selfId = saved?.id || editing?.id

      // Aplicar cambios en hijos — solo tiene sentido si YO no tengo padre.
      if (!parentId && selfId) {
        const previousChildren = childrenOf(selfId).map((c) => c.id)
        const toAdd = childIds.filter((id) => !previousChildren.includes(id))
        const toRemove = previousChildren.filter((id) => !childIds.includes(id))

        for (const id of toAdd) {
          const child = byId.get(id)
          if (!child) continue
          await apiCall(`/clients/${id}`, {
            method: 'PUT',
            body: { ...child, parentClientId: selfId },
          })
        }
        for (const id of toRemove) {
          const child = byId.get(id)
          if (!child) continue
          await apiCall(`/clients/${id}`, {
            method: 'PUT',
            body: { ...child, parentClientId: null },
          })
        }
      }

      toast.success(editing?.id ? 'Cliente actualizado' : 'Cliente creado')
      closeModal()
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelete = async (id) => {
    const children = childrenOf(id)
    const warn = children.length
      ? `Este cliente tiene ${children.length} cliente(s) final(es). Al eliminarlo, quedarán desvinculados (no se borrarán). ¿Continuar?`
      : '¿Eliminar este cliente?'
    if (!confirm(warn)) return
    try {
      await remove.mutate(id)
      toast.success('Cliente eliminado')
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  return (
    <div>
      <PageHeader
        title="Clientes"
        subtitle="Clientes Principales → Consignatarios ligados (ubicaciones de envío)"
        icon={Users}
        action={
          <Button onClick={openCreate}>
            <Plus size={20} />
            <span>Nuevo cliente</span>
          </Button>
        }
      />

      {loading && <p className="text-gray-500">Cargando…</p>}
      {error && <p className="text-red-600">Error: {error.message}</p>}
      {data && (
        <ExpandableCards
          items={data}
          getKey={(r) => r.id}
          emptyText="Sin clientes registrados"
          renderHeader={(r) => {
            const kids = childrenOf(r.id)
            const parent = r.parentClientId ? byId.get(r.parentClientId) : null
            return (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold text-expo-dark">{r.name}</span>
                <span className="text-gray-500 text-sm">{r.id}</span>
                <span className="text-gray-500 text-sm">{r.email || '—'}</span>
                {parent && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700"
                    title={`Cliente final de ${parent.name}`}
                  >
                    <Link2 size={12} />
                    hijo de {parent.name}
                  </span>
                )}
                {kids.length > 0 && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700"
                    title="Mayorista con clientes finales"
                  >
                    <Link2 size={12} />
                    {kids.length} final{kids.length === 1 ? '' : 'es'}
                  </span>
                )}
                <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-expo-primary/10 text-expo-primary">
                  {labelForType(r.clientType)}
                </span>
              </div>
            )
          }}
          renderDetails={(r) => {
            const kids = childrenOf(r.id)
            const parent = r.parentClientId ? byId.get(r.parentClientId) : null
            return (
              <div className="space-y-4 pt-4">
                <CardGrid cols={3}>
                  <CardField label="ID" value={r.id} />
                  <CardField label="Email" value={r.email} />
                  <CardField label="Teléfono" value={r.phone} />
                  <CardField label="NIT" value={r.nit} />
                  <CardField label="Dirección" value={r.address} />
                  <CardField label="Tipo" value={labelForType(r.clientType)} />
                </CardGrid>

                {/* Jerarquía */}
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="text-sm font-semibold text-expo-dark mb-2 flex items-center gap-2">
                    <Link2 size={16} />
                    Jerarquía
                  </div>
                  {parent ? (
                    <div className="text-sm text-gray-700">
                      Este cliente es <strong>consignatario</strong> de{' '}
                      <span className="font-semibold text-expo-primary">
                        {parent.name}
                      </span>{' '}
                      <span className="text-gray-500">({parent.id})</span>.
                    </div>
                  ) : kids.length > 0 ? (
                    <>
                      <div className="text-sm text-gray-700 mb-2">
                        Cliente <strong>principal</strong> con{' '}
                        {kids.length} consignatario{kids.length === 1 ? '' : 's'}:
                      </div>
                      <ul className="space-y-1">
                        {kids.map((k) => (
                          <li
                            key={k.id}
                            className="text-sm flex items-center gap-2 text-gray-700"
                          >
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-100 text-indigo-700 font-semibold">
                              {k.id}
                            </span>
                            <span className="font-medium">{k.name}</span>
                            {k.address && (
                              <span className="text-gray-500 text-xs">· {k.address}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Sin jerarquía (cliente directo).
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={() => openEdit(r)}>
                    <Pencil size={16} />
                    <span>Editar</span>
                  </Button>
                  <Button type="button" variant="danger" onClick={() => onDelete(r.id)}>
                    <Trash2 size={16} />
                    <span>Eliminar</span>
                  </Button>
                </div>
              </div>
            )
          }}
        />
      )}

      <Modal
        open={!!editing}
        onClose={closeModal}
        title={editing?.id ? 'Editar cliente' : 'Nuevo cliente'}
        size="lg"
      >
        {editing && (
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Nombre" required>
              <Input name="name" defaultValue={editing.name} required />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email">
                <Input type="email" name="email" defaultValue={editing.email} />
              </Field>
              <Field label="Teléfono">
                <Input name="phone" defaultValue={editing.phone} />
              </Field>
            </div>
            <Field label="NIT / ID fiscal">
              <Input name="nit" defaultValue={editing.nit} />
            </Field>
            <Field label="Dirección">
              <Input name="address" defaultValue={editing.address} />
            </Field>
            <Field
              label="Tipo de cliente"
              hint="Solo dos categorías: Principal o Consignatario."
            >
              {/*
                Selector visual con dos tarjetas-botón. Más rápido y
                claro que un dropdown — el usuario ve ambas opciones a
                la vez con icono propio y elige con un click.
              */}
              <div className="grid grid-cols-2 gap-3">
                {CLIENT_TYPE_OPTIONS.map((opt) => {
                  const Icon = opt.icon
                  const active = clientType === opt.id
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setClientType(opt.id)
                        // Si elige "Principal", se quita el parent
                        // automáticamente (un Principal no tiene padre).
                        if (opt.id === 'principal' && parentId) {
                          setParentId('')
                        }
                      }}
                      className={
                        'relative text-left rounded-xl border-2 p-3 transition-all ' +
                        (active
                          ? 'border-expo-primary bg-expo-primary/10 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-expo-primary/50 hover:bg-expo-light/40')
                      }
                    >
                      {active && (
                        <Check
                          size={16}
                          className="absolute top-2 right-2 text-expo-primary"
                        />
                      )}
                      <div className="flex items-center gap-3">
                        <div
                          className={
                            'p-2 rounded-lg ' +
                            (active
                              ? 'bg-expo-primary text-white'
                              : 'bg-expo-light text-expo-primary')
                          }
                        >
                          <Icon size={20} />
                        </div>
                        <div>
                          <div className="font-semibold text-expo-dark">
                            {opt.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {opt.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </Field>

            {/* ─── Jerarquía ──────────────────────────────────────────── */}
            <div className="rounded-xl border border-gray-200 bg-expo-light/30 p-4 space-y-3">
              <div className="text-sm font-semibold text-expo-dark flex items-center gap-2">
                <Link2 size={16} />
                Jerarquía (Principal → Consignatarios)
              </div>

              <Field label="Pertenece a (cliente Principal)">
                <Select
                  value={parentId}
                  onChange={(e) => {
                    const v = e.target.value
                    setParentId(v)
                    if (v) setChildIds([]) // un consignatario no puede tener consignatarios
                  }}
                  disabled={childIds.length > 0}
                >
                  <option value="">— Ninguno (cliente Principal) —</option>
                  {parentCandidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.id})
                    </option>
                  ))}
                </Select>
              </Field>
              {childIds.length > 0 && (
                <p className="text-xs text-gray-500 -mt-1">
                  Este cliente ya tiene consignatarios ligados, por eso no puede
                  a su vez pertenecer a otro.
                </p>
              )}

              {!parentId && (
                <div>
                  <div className="text-sm font-medium text-expo-dark mb-1">
                    Consignatarios ligados a este Principal
                  </div>
                  <div className="flex gap-2 mb-2">
                    <Select
                      className="flex-1"
                      value={childPick}
                      onChange={(e) => setChildPick(e.target.value)}
                    >
                      <option value="">Seleccionar consignatario…</option>
                      {childCandidates.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.id})
                        </option>
                      ))}
                    </Select>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={addChild}
                      disabled={!childPick}
                    >
                      <Plus size={14} />
                      <span>Añadir</span>
                    </Button>
                  </div>
                  {childIds.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Sin consignatarios ligados. Añade aquí las ubicaciones o
                      consignatarios a los que se envía directamente.
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {childIds.map((id) => {
                        const c = byId.get(id)
                        return (
                          <li
                            key={id}
                            className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                          >
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-100 text-indigo-700 font-semibold">
                              {id}
                            </span>
                            <span className="font-medium text-expo-dark">
                              {c?.name || '(desconocido)'}
                            </span>
                            <button
                              type="button"
                              className="ml-auto p-1 text-gray-400 hover:text-red-600"
                              onClick={() => removeChild(id)}
                              title="Quitar"
                            >
                              <Link2Off size={14} />
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={closeModal}>
                <X size={16} />
                <span>Cancelar</span>
              </Button>
              <Button type="submit" disabled={save.loading}>
                {save.loading ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
